import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { FormSpy, useField } from 'react-final-form';

import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import {
  getKnmFormRef,
  getKnmIsNeedActSaveOrSign,
  getKnmIsWatchOnly,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { VIDEO_CALL_INFO_WRAPPER_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { PmFieldMark } from 'KnmPmPackage/MainModule/components/PmFieldMark';
import { IKnmErknmFormRescheduling } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRescheduling';
import { IKnmPmFormRescheduling } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRescheduling';

import styles from './VideoCallInfo.module.scss';
import { IVideoCallInfo } from '../../../../APIPackage/CatalogsModule/interfaces/IVideoCallInfo';
import { ErknmFieldMark } from '../../../MainModule/components/ErknmFieldMark';
import { checkHasErrorInCollapseBlock } from '../../../../functions/checkHasErrorInCollapseBlock';

interface IVideoCallInfoProps {
  isRequiredSubject: boolean;
  isPm?: boolean;
  rescheduling?: IKnmErknmFormRescheduling[] | IKnmPmFormRescheduling[] | null;
}

interface IVideoCallInfoStoreProps {
  formRef: IKnmFormRef;
  isProsecutorOrSuperProsecutor: boolean;
  isWatchOnly: boolean;
  isNeedActSaveOrSign?: boolean;
}

const VideoCallInfoWithoutStore = ({
  isRequiredSubject,
  formRef,
  isProsecutorOrSuperProsecutor,
  isWatchOnly,
  isPm,
  isNeedActSaveOrSign = false,
  rescheduling,
}: IVideoCallInfoProps & IVideoCallInfoStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addField = (fieldName: string) => () =>
    !isWatchOnly &&
    !isProsecutorOrSuperProsecutor &&
    push &&
    push(fieldName, { listId: uuidv4(), isNew: true, showLinkLikeHypelink: false });
  const removeField = (fieldName: string, index: number) => () =>
    !isWatchOnly && !isProsecutorOrSuperProsecutor && remove && remove(fieldName, index);

  const startDate: string | undefined = useField('startDate')?.input?.value;
  const stopDate: string | undefined = useField('stopDate')?.input?.value;

  const minDate = useMemo(() => {
    const parsedStartDate = startDate
      ? parseDate({
          date: startDate,
          parseFormat: DATE_FORMAT_LONG_FNS,
        })
      : undefined;

    if (rescheduling && rescheduling?.[0]?.newStartDate) {
      const reschedulingNewDate = parseDate({
        date: rescheduling?.[0]?.newStartDate,
        parseFormat: DATE_FORMAT_LONG_FNS,
      });

      return reschedulingNewDate;
    }

    return parsedStartDate;
  }, [startDate, rescheduling]);

  const maxDate = useMemo(() => {
    const parsedStopDate = stopDate
      ? parseDate({
          date: stopDate,
          parseFormat: DATE_FORMAT_LONG_FNS,
        })
      : undefined;

    if (rescheduling && rescheduling?.[0]?.newEndDate) {
      const reschedulingNewDate = parseDate({
        date: rescheduling?.[0]?.newEndDate,
        parseFormat: DATE_FORMAT_LONG_FNS,
      });

      if (reschedulingNewDate) {
        reschedulingNewDate.setHours(23);
        reschedulingNewDate.setMinutes(59);
      }

      return reschedulingNewDate;
    }

    if (parsedStopDate) {
      parsedStopDate.setHours(23);
      parsedStopDate.setMinutes(59);
    }

    return parsedStopDate;
  }, [stopDate, rescheduling]);

  const openLink = useCallback((e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>, link: string | undefined) => {
    let newLink = link;

    // @ts-ignore
    if (!e.target.closest('.ignoreEvents') && link) {
      if (link?.indexOf('https') === -1 && link?.indexOf('http') === -1) {
        newLink = `https://${link}`;
      }

      window.open(newLink, '_blank');
    }
  }, []);

  return (
    <>
      <Row>
        <FieldBlock
          label={
            <>
              Видео-конференц-связь
              {isPm ? <PmFieldMark fieldName="videoCallInfo" /> : <ErknmFieldMark fieldName="videoCallInfo" />}
            </>
          }
          id={VIDEO_CALL_INFO_WRAPPER_BLOCK_ID}
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              id="erknmMeeteingsAddButton"
              disabled={isWatchOnly || isNeedActSaveOrSign || isProsecutorOrSuperProsecutor}
              onClick={addField('videoCallInfo')}
            />
          }
        >
          <FieldArray name="videoCallInfo" key={isRequiredSubject ? 0 : 1}>
            {({ fields }: FieldArrayRenderProps<IVideoCallInfo, HTMLElement>) => {
              return fields.map((name, index) => {
                const fieldValue: IVideoCallInfo = fields.value?.[index];

                const hasError = checkHasErrorInCollapseBlock(formRef, name);

                return (
                  <KnmCollapse
                    id={`videoCallInfo[${fieldValue.listId}]`}
                    key={`videoCallInfo[${fieldValue.listId}]`}
                    theme="fields"
                    title={`Встреча №${index + 1}`}
                    isNew={fieldValue.isNew}
                    opened={fieldValue.isNew}
                    onClose={
                      !isWatchOnly && !isNeedActSaveOrSign && !isProsecutorOrSuperProsecutor
                        ? removeField('videoCallInfo', index)
                        : undefined
                    }
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [name]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[name]}
                  >
                    <>
                      <Row id={name}>
                        <FieldBlock
                          label={
                            <>
                              Ссылка на ВКС
                              {isPm ? (
                                <PmFieldMark fieldName={`${name}.link`} />
                              ) : (
                                <ErknmFieldMark fieldName={`${name}.link`} />
                              )}
                            </>
                          }
                          color="black"
                          type="not-space"
                          size="half"
                        >
                          <div
                            className={styles.ErknmVideoCallInfoContainerHyperlink}
                            onClick={fieldValue.showLinkLikeHypelink ? e => openLink(e, fieldValue.link) : undefined}
                          >
                            <TextInputField
                              className={
                                fieldValue.showLinkLikeHypelink
                                  ? styles.ErknmVideoCallInfoHyperlink
                                  : styles.ErknmVideoCallInfoInput
                              }
                              id={`${name}.link`}
                              name={`${name}.link`}
                              placeholder="Введите ссылку"
                              disabled={isWatchOnly || isProsecutorOrSuperProsecutor || isNeedActSaveOrSign}
                            />
                            {fieldValue.showLinkLikeHypelink ? (
                              <button
                                className={`ignoreEvents ${styles.FilterNameEditButton}`}
                                type="button"
                                onClick={() => {
                                  if (!isWatchOnly && !isProsecutorOrSuperProsecutor) {
                                    // @ts-ignore
                                    formRef.current.change(`${name}.showLinkLikeHypelink`, false);
                                  }
                                }}
                              />
                            ) : (
                              <button
                                className={styles.FilterNameSaveButton}
                                type="button"
                                onClick={() => {
                                  if (fieldValue.link && !isWatchOnly && !isProsecutorOrSuperProsecutor) {
                                    // @ts-ignore
                                    formRef.current.change(`${name}.showLinkLikeHypelink`, true);
                                  }
                                }}
                              />
                            )}
                          </div>
                        </FieldBlock>
                        <FieldBlock
                          label={
                            <>
                              Дата и время проведения ВКС
                              {isPm ? (
                                <PmFieldMark fieldName={`${name}.dateTime`} />
                              ) : (
                                <ErknmFieldMark fieldName={`${name}.dateTime`} />
                              )}
                            </>
                          }
                          color="black"
                          type="not-space"
                          size="half"
                        >
                          <DatePickerField
                            className={styles.ErknmVideoCallInfoInput}
                            id={`${name}.dateTime`}
                            name={`${name}.dateTime`}
                            disabled={isWatchOnly || isProsecutorOrSuperProsecutor || isNeedActSaveOrSign}
                            timeFormat
                            minDate={minDate}
                            maxDate={maxDate}
                          />
                        </FieldBlock>
                      </Row>
                      <Row>
                        <FieldBlock label="Инструкция" color="black" type="not-space">
                          <TextInputField
                            className={styles.ErknmVideoCallInfoInput}
                            id={`${name}.instruction`}
                            name={`${name}.instruction`}
                            placeholder="В случае использования специального ПО приложить ссылку на инструкцию"
                            disabled={isWatchOnly || isProsecutorOrSuperProsecutor || isNeedActSaveOrSign}
                          />
                        </FieldBlock>
                      </Row>
                    </>
                  </KnmCollapse>
                );
              });
            }}
          </FieldArray>
        </FieldBlock>
      </Row>

      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          let error: string | undefined;

          if (props.errors?.videoCallInfo) {
            Object.keys(props.errors?.videoCallInfo).forEach(key => {
              if (key === 'FINAL_FORM/array-error') {
                error = props.errors?.videoCallInfo[key];
              }
            });
          }

          if (error) {
            return <p className={`shared-input-error ${styles.VideoCallInfoErrorMsg}`}>{error}</p>;
          }

          return null;
        }}
      </FormSpy>
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  formRef: getKnmFormRef(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
});

export const VideoCallInfo = connect(mapStateToProps)(VideoCallInfoWithoutStore);
